import React, { useEffect, useState } from 'react';
import { Mail, Eye, Reply, Trash2, CheckCircle, Clock, AlertCircle, X, Send } from 'lucide-react';
import api from '../../services/api';
import toast from 'react-hot-toast';

interface ContactQuery {
  id: number;
  name: string;
  email: string;
  subject: string;
  message: string;
  status: 'new' | 'read' | 'answered';
  admin_reply: string | null;
  replied_at: string | null;
  created_at: string;
}

const statusConfig = {
  new: { label: 'New', color: 'bg-red-100 text-red-700', icon: AlertCircle },
  read: { label: 'Read', color: 'bg-yellow-100 text-yellow-700', icon: Clock },
  answered: { label: 'Answered', color: 'bg-green-100 text-green-700', icon: CheckCircle },
};

export default function AdminContacts() {
  const [queries, setQueries] = useState<ContactQuery[]>([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<ContactQuery | null>(null);
  const [reply, setReply] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [filter, setFilter] = useState<'all' | 'new' | 'read' | 'answered'>('all');

  const fetchQueries = async () => {
    setLoading(true);
    try {
      const res = await api.get('/admin/contacts');
      setQueries(res.data.data || res.data);
    } catch {
      toast.error('Failed to load queries');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchQueries(); }, []);

  const openQuery = async (q: ContactQuery) => {
    setSelected(q);
    setReply(q.admin_reply || '');
    if (q.status === 'new') {
      try {
        await api.get(`/admin/contacts/${q.id}`);
        setQueries(prev => prev.map(item => item.id === q.id ? { ...item, status: 'read' } : item));
        setSelected(prev => prev ? { ...prev, status: 'read' } : null);
      } catch {}
    }
  };

  const handleReply = async () => {
    if (!selected || !reply.trim()) return;
    setSubmitting(true);
    try {
      const res = await api.put(`/admin/contacts/${selected.id}`, { admin_reply: reply });
      toast.success('Reply saved!');
      setQueries(prev => prev.map(item => item.id === selected.id ? res.data.query : item));
      setSelected(res.data.query);
    } catch {
      toast.error('Failed to save reply');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Delete this query?')) return;
    try {
      await api.delete(`/admin/contacts/${id}`);
      setQueries(prev => prev.filter(q => q.id !== id));
      if (selected?.id === id) setSelected(null);
      toast.success('Deleted');
    } catch {
      toast.error('Failed to delete');
    }
  };

  const formatDate = (d: string) => new Date(d).toLocaleDateString('en-PK', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });

  const filtered = filter === 'all' ? queries : queries.filter(q => q.status === filter);
  const counts = { all: queries.length, new: queries.filter(q => q.status === 'new').length, read: queries.filter(q => q.status === 'read').length, answered: queries.filter(q => q.status === 'answered').length };

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-gray-900">Contact Queries</h2>
          <p className="text-sm text-gray-500 mt-0.5">Manage customer messages and inquiries</p>
        </div>
        <div className="flex items-center gap-2">
          {counts.new > 0 && (
            <span className="bg-red-100 text-red-700 text-xs font-bold px-3 py-1 rounded-full">{counts.new} new</span>
          )}
        </div>
      </div>

      {/* Filter Tabs */}
      <div className="flex gap-2 flex-wrap">
        {(['all', 'new', 'read', 'answered'] as const).map(f => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            className={`px-4 py-2 rounded-lg text-xs font-semibold uppercase tracking-wide transition-colors ${filter === f ? 'bg-gray-900 text-white' : 'bg-white text-gray-600 border border-gray-200 hover:border-gray-400'}`}
          >
            {f} ({counts[f]})
          </button>
        ))}
      </div>

      <div className="flex gap-5 min-h-[500px]">
        {/* Query List */}
        <div className={`flex-shrink-0 ${selected ? 'w-[340px] hidden md:flex flex-col' : 'flex-1 flex flex-col'}`}>
          {loading ? (
            <div className="flex items-center justify-center h-40">
              <div className="w-7 h-7 border-2 border-gray-300 border-t-gray-700 rounded-full animate-spin" />
            </div>
          ) : filtered.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-40 bg-white rounded-xl border border-gray-100 text-center">
              <Mail size={32} className="text-gray-300 mb-2" />
              <p className="text-sm text-gray-400">No queries found</p>
            </div>
          ) : (
            <div className="space-y-2">
              {filtered.map(q => {
                const status = statusConfig[q.status];
                const StatusIcon = status.icon;
                return (
                  <div
                    key={q.id}
                    onClick={() => openQuery(q)}
                    className={`bg-white rounded-xl border p-4 cursor-pointer hover:shadow-md transition-all ${selected?.id === q.id ? 'border-gray-900 ring-1 ring-gray-900' : 'border-gray-100'} ${q.status === 'new' ? 'border-l-4 border-l-red-500' : ''}`}
                  >
                    <div className="flex items-start justify-between gap-2 mb-1.5">
                      <p className="text-sm font-semibold text-gray-900 truncate flex-1">{q.name}</p>
                      <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full flex items-center gap-1 flex-shrink-0 ${status.color}`}>
                        <StatusIcon size={10} /> {status.label}
                      </span>
                    </div>
                    <p className="text-xs font-medium text-gray-700 truncate mb-1">{q.subject}</p>
                    <p className="text-xs text-gray-400 truncate mb-2">{q.message}</p>
                    <div className="flex items-center justify-between">
                      <p className="text-[11px] text-gray-400">{formatDate(q.created_at)}</p>
                      <button
                        onClick={e => { e.stopPropagation(); handleDelete(q.id); }}
                        className="p-1 text-gray-300 hover:text-red-500 transition-colors"
                        title="Delete"
                        aria-label="Delete query"
                      >
                        <Trash2 size={13} />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Detail / Reply Panel */}
        {selected && (
          <div className="flex-1 bg-white rounded-xl border border-gray-100 flex flex-col min-w-0">
            {/* Detail Header */}
            <div className="flex items-start justify-between px-6 py-4 border-b border-gray-100">
              <div>
                <h3 className="font-bold text-gray-900">{selected.subject}</h3>
                <p className="text-sm text-gray-500 mt-0.5">{selected.name} &bull; <a href={`mailto:${selected.email}`} className="text-blue-600 hover:underline">{selected.email}</a></p>
                <p className="text-xs text-gray-400 mt-0.5">{formatDate(selected.created_at)}</p>
              </div>
              <button onClick={() => setSelected(null)} aria-label="Close" className="p-1.5 text-gray-400 hover:text-gray-700 transition-colors rounded-lg hover:bg-gray-100">
                <X size={16} />
              </button>
            </div>

            {/* Message */}
            <div className="px-6 py-5 flex-1 overflow-y-auto space-y-5">
              <div>
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Customer Message</p>
                <div className="bg-gray-50 rounded-xl p-4 text-sm text-gray-700 leading-relaxed whitespace-pre-wrap">
                  {selected.message}
                </div>
              </div>

              {selected.admin_reply && (
                <div>
                  <p className="text-xs font-semibold text-green-600 uppercase tracking-wider mb-2 flex items-center gap-1.5">
                    <CheckCircle size={12} /> Your Previous Reply {selected.replied_at && `• ${formatDate(selected.replied_at)}`}
                  </p>
                  <div className="bg-green-50 rounded-xl p-4 text-sm text-green-800 leading-relaxed whitespace-pre-wrap border border-green-100">
                    {selected.admin_reply}
                  </div>
                </div>
              )}

              {/* Reply Box */}
              <div>
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2 flex items-center gap-1.5">
                  <Reply size={12} /> {selected.admin_reply ? 'Update Reply' : 'Write Reply'}
                </p>
                <textarea
                  rows={5}
                  value={reply}
                  onChange={e => setReply(e.target.value)}
                  placeholder="Write your reply to the customer here..."
                  className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm text-gray-800 focus:outline-none focus:border-gray-900 resize-none bg-gray-50"
                />
                <div className="flex justify-end mt-2">
                  <button
                    onClick={handleReply}
                    disabled={submitting || !reply.trim()}
                    className="flex items-center gap-2 bg-gray-900 text-white px-5 py-2.5 text-xs font-bold uppercase tracking-wide rounded-lg hover:bg-gray-700 transition-colors disabled:opacity-50"
                  >
                    {submitting ? <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <Send size={13} />}
                    {selected.admin_reply ? 'Update Reply' : 'Send Reply'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {!selected && !loading && filtered.length > 0 && (
          <div className="hidden md:flex flex-col flex-1 items-center justify-center bg-white rounded-xl border border-dashed border-gray-200 text-center p-10">
            <Eye size={40} className="text-gray-200 mb-3" />
            <p className="text-sm text-gray-400">Select a query to view details</p>
          </div>
        )}
      </div>
    </div>
  );
}
